import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Icon } from '@alycecom/ui';

import InformationCard, { IInformationCardProps } from '../../../../components/InformationCard/InformationCard';
import useResponsive from '../../../../hooks/useResponsive';
import productTypes from '../../constants/productTypes.constants';
import { IProduct } from '../../types/Product.types';
import { IAlyceCustomTheme } from '../../../../styles/alyceTheme';
import { TFullSender } from '../../../../store/users/users.types';

const useStyles = makeStyles<IAlyceCustomTheme, { product: IProduct }>(({ palette, spacing }) => ({
  title: {
    textAlign: 'left',
    lineHeight: 1.33,
    color: palette.grey.main,
  },
  hint: {
    color: palette.customStyling.primaryCtaColor,
    fontWeight: 'bold',
  },
  deliverTime: {
    color: palette.green.fruitSalad,
    fontWeight: 'bold',
  },
  deliverTimeMobile: {
    fontSize: '13px !important',
  },
  truckIcon: {
    width: 20,
    height: 20,
    marginLeft: spacing(1 / 2),
  },
  textWrap: {
    wordBreak: 'break-word',
    wordWrap: 'break-word',
  },
  boxImage: {
    backgroundImage: props => `url(${props.product.image})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
}));

export interface IGiftInfoCardProps extends IInformationCardProps {
  product: IProduct;
  sender: TFullSender | null;
  hint?: string;
  gl?: string;
  showDeliverTime?: boolean;
  children?: React.ReactNode;
  productBoxProps?: Record<string, unknown>;
}

const GiftInfoCard = ({
  product,
  sender,
  hint = '',
  gl = 'gift',
  showDeliverTime = false,
  children,
  productBoxProps = {},
  ...infoCardProps
}: IGiftInfoCardProps) => {
  const { isDesktop } = useResponsive();
  const classes = useStyles({ product });
  const imageDimension = [56, 112];

  const title = sender ? `Your ${gl} from ${sender.firstName}` : '';
  const nameClass = isDesktop ? 'H4-Dark-Bold' : 'Body-Regular-Left-Static-Bold';

  const subTitle = useMemo(() => {
    const isGiftCard = product.typeId === productTypes.TYPE_ID_GIFT_CARD;
    return (
      <>
        {isGiftCard && (
          <Typography className={classNames('Body-Regular-Left-Static', { [classes.deliverTimeMobile]: !isDesktop })}>
            This will ship in approximately &nbsp;
            <span className={classes.deliverTime}>{product.isTangoCard ? 'instant' : '24 hours'}</span>
            <Icon className={classes.truckIcon} icon="truck" color="green.fruitSalad" />
          </Typography>
        )}
      </>
    );
  }, [classes, isDesktop, product]);

  return (
    <Box maxWidth={[320, 430]} mx="auto" position="relative" data-testid="giftInfoCard">
      <InformationCard title={title} subTitle={showDeliverTime && subTitle} actionText="Change" {...infoCardProps}>
        <Box width={1} display="flex" justifyContent="flex-start" alignItems="flex-start" {...productBoxProps}>
          <Box
            className={classes.boxImage}
            width={imageDimension}
            minWidth={imageDimension}
            height={imageDimension}
            mr={2}
          />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            alignItems="flex-start"
            data-testid="choiceProductDetails"
          >
            <Box textAlign="left" maxWidth={250}>
              {!product.hasExternalFulfillment && (
                <Typography className={classNames('Body-Small-Inactive', classes.title, classes.textWrap)}>
                  {product.provider}
                </Typography>
              )}
              <Typography className={classNames(nameClass, classes.textWrap)}>{product.name}</Typography>
            </Box>
            {product.options && (
              <Box mt={1} maxWidth={150} textAlign="left">
                <Typography className="Body-Regular-Left-Static-Bold">
                  Option: <span className="Body-Regular-Left-Inactive">{product.options}</span>
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {children}
      </InformationCard>
      {hint && (
        <Box
          position="absolute"
          top={0}
          width="auto"
          height={34}
          p="4px"
          bgcolor="customStyling.primaryColor"
          borderRadius="5px 0 5px 0"
        >
          <Typography className={classes.hint}>{hint}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default memo(GiftInfoCard);
